import { useEffect, useRef, useState, type ChangeEventHandler } from "react";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import {
  Camera as CameraIcon,
  Download,
  FilePlus2,
  FileText,
  FolderOpen,
  Images,
  Loader2,
  ShieldCheck,
  Trash2,
  UploadCloud
} from "lucide-react";

import api from "../../api/Client";
import { useSileo } from "../../components/Sileo";
import styles from "./DocumentPage.module.css";

type CustomerDocument = {
  document_id?: number;
  document: string;
  document_name: string;
  created_at?: string;
};

type DocumentsResponse = {
  documents?: CustomerDocument[];
};

type PendingDocument = {
  name: string;
  blob: Blob;
  size: number;
};

const ALLOWED_ACCEPT = ".pdf,.png,.jpg,.jpeg,.doc,.docx";

export default function DocumentPage() {
  const sileo = useSileo();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [documents, setDocuments] = useState<CustomerDocument[]>([]);
  const [selectedFile, setSelectedFile] = useState<PendingDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void loadDocuments();
  }, []);

  async function loadDocuments(options?: { silent?: boolean }) {
    const silent = Boolean(options?.silent);

    if (!silent) {
      setLoading(true);
    }

    setError(null);

    try {
      const response = await api.get<DocumentsResponse>("/api/customers/documents");
      const nextDocuments = Array.isArray(response.data?.documents) ? response.data.documents : [];
      setDocuments(nextDocuments);
    } catch (requestError: any) {
      const message = requestError?.response?.data?.error || "No se pudieron cargar los documentos.";
      setError(message);
      if (!silent) {
        sileo.error(message, { durationMs: 2400 });
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }

  function openFilePicker() {
    fileInputRef.current?.click();
  }

  const onFilesSelected: ChangeEventHandler<HTMLInputElement> = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setSelectedFile({
      name: file.name,
      blob: file,
      size: file.size
    });
  };

  async function pickFromCamera(source: CameraSource) {
    try {
      const photo = await Camera.getPhoto({
        source,
        resultType: CameraResultType.Base64,
        quality: 88
      });

      if (!photo.base64String) {
        return;
      }

      const normalizedFormat = normalizeCameraFormat(photo.format);
      const blob = b64ToBlob(photo.base64String, normalizedFormat.mimeType);
      setSelectedFile({
        name: `document-${Date.now()}.${normalizedFormat.extension}`,
        blob,
        size: blob.size
      });
    } catch {
      sileo.info("No se pudo abrir esa fuente ahora mismo.", { durationMs: 2200 });
    }
  }

  async function uploadSelectedFile() {
    if (!selectedFile) {
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile.blob, selectedFile.name);

      await api.post("/api/customers/documents", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      await loadDocuments({ silent: true });
      sileo.success("Documento subido correctamente.", { durationMs: 2200 });
    } catch (requestError: any) {
      const message = requestError?.response?.data?.error || "No se pudo subir el documento.";
      sileo.error(message, { durationMs: 2400 });
    } finally {
      setUploading(false);
    }
  }

  async function downloadDocument(documentItem: CustomerDocument) {
    setBusyKey(`download:${documentItem.document}`);

    try {
      const response = await api.get(`/api/customers/documents/${encodeURIComponent(documentItem.document)}`, {
        responseType: "blob"
      });

      const blob = new Blob([response.data]);
      const blobUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      anchor.download = documentItem.document_name || documentItem.document;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(blobUrl);

      sileo.info("Descarga iniciada.", { durationMs: 2000 });
    } catch (requestError: any) {
      const message = requestError?.response?.data?.error || "No se pudo descargar el documento.";
      sileo.error(message, { durationMs: 2400 });
    } finally {
      setBusyKey(null);
    }
  }

  async function deleteDocument(documentItem: CustomerDocument) {
    const confirmed = window.confirm(`Quieres eliminar "${documentItem.document_name}"?`);
    if (!confirmed) {
      return;
    }

    setBusyKey(`delete:${documentItem.document}`);

    try {
      await api.delete(`/api/customers/documents/${encodeURIComponent(documentItem.document)}`);
      setDocuments((current) => current.filter((entry) => entry.document !== documentItem.document));
      sileo.success("Documento eliminado.", { durationMs: 2200 });
    } catch (requestError: any) {
      const message = requestError?.response?.data?.error || "No se pudo eliminar el documento.";
      sileo.error(message, { durationMs: 2400 });
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <main className={styles.page}>
      <section className={styles.hero}>
        <div className={styles.heroBadge}>
          <ShieldCheck size={14} />
          Documentacion privada
        </div>
        <h1>Sube informes, analiticas o documentos utiles</h1>
        <p>
          El equipo podra revisarlos dentro de tu historial. Admitimos PDF, imagenes y documentos Word hasta 10MB.
        </p>
        <div className={styles.heroStats}>
          <div className={styles.statCard}>
            <span>Archivos</span>
            <strong>{documents.length}</strong>
          </div>
          <div className={styles.statCard}>
            <span>Estado</span>
            <strong>Seguro</strong>
          </div>
        </div>
      </section>

      <section className={styles.card}>
        <header className={styles.cardHeader}>
          <div>
            <h2>Nuevo documento</h2>
            <p>Elige la fuente que te resulte mas comoda y subelo en un toque.</p>
          </div>
          <div className={styles.headerIcon}>
            <UploadCloud size={18} />
          </div>
        </header>

        <div className={styles.actionsGrid}>
          <button type="button" className={styles.primaryAction} onClick={() => void pickFromCamera(CameraSource.Camera)}>
            <CameraIcon size={16} />
            Camara
          </button>
          <button type="button" className={styles.secondaryAction} onClick={() => void pickFromCamera(CameraSource.Photos)}>
            <Images size={16} />
            Galeria
          </button>
          <button type="button" className={styles.secondaryAction} onClick={openFilePicker}>
            <FolderOpen size={16} />
            Archivos
          </button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          className={styles.hiddenInput}
          accept={ALLOWED_ACCEPT}
          onChange={onFilesSelected}
        />

        {selectedFile ? (
          <div className={styles.selectedFile}>
            <div className={styles.fileVisual}>
              <FilePlus2 size={18} />
            </div>
            <div className={styles.selectedMeta}>
              <strong>{selectedFile.name}</strong>
              <span>{formatFileSize(selectedFile.size)}</span>
            </div>
            <div className={styles.selectedActions}>
              <button
                type="button"
                className={styles.ghostButton}
                onClick={() => {
                  setSelectedFile(null);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                  }
                }}
                disabled={uploading}
              >
                Quitar
              </button>
              <button
                type="button"
                className={styles.confirmButton}
                onClick={() => void uploadSelectedFile()}
                disabled={uploading}
              >
                {uploading ? <Loader2 size={16} className={styles.spin} /> : <UploadCloud size={16} />}
                {uploading ? "Subiendo..." : "Subir ahora"}
              </button>
            </div>
          </div>
        ) : (
          <div className={styles.helperBox}>
            <p>Ningun archivo seleccionado todavia.</p>
            <span>Formatos permitidos: PDF, PNG, JPG, DOC y DOCX.</span>
          </div>
        )}
      </section>

      <section className={styles.card}>
        <header className={styles.cardHeader}>
          <div>
            <h2>Tus documentos</h2>
            <p>Gestiona lo que ya has compartido con el centro.</p>
          </div>
          <div className={styles.counter}>{documents.length}</div>
        </header>

        {loading && (
          <div className={styles.stateRow}>
            <Loader2 size={16} className={styles.spin} />
            <span>Cargando documentos...</span>
          </div>
        )}

        {!loading && error && <p className={styles.errorText}>{error}</p>}

        {!loading && !error && documents.length === 0 && (
          <div className={styles.emptyState}>
            <FileText size={20} />
            <strong>Aun no has subido documentos</strong>
            <span>Cuando los anadas aqui, quedaran disponibles para seguimiento profesional.</span>
          </div>
        )}

        {!loading && !error && documents.length > 0 && (
          <div className={styles.documentList}>
            {documents.map((documentItem) => {
              const downloading = busyKey === `download:${documentItem.document}`;
              const deleting = busyKey === `delete:${documentItem.document}`;

              return (
                <article key={documentItem.document} className={styles.documentItem}>
                  <div className={styles.documentIcon}>
                    <FileText size={18} />
                  </div>
                  <div className={styles.documentMeta}>
                    <strong>{documentItem.document_name}</strong>
                    <span>{formatDocumentDate(documentItem.created_at)}</span>
                  </div>
                  <div className={styles.documentActions}>
                    <button
                      type="button"
                      className={styles.inlineButton}
                      onClick={() => void downloadDocument(documentItem)}
                      disabled={Boolean(busyKey)}
                    >
                      {downloading ? <Loader2 size={16} className={styles.spin} /> : <Download size={16} />}
                      Descargar
                    </button>
                    <button
                      type="button"
                      className={styles.deleteButton}
                      onClick={() => void deleteDocument(documentItem)}
                      disabled={Boolean(busyKey)}
                    >
                      {deleting ? <Loader2 size={16} className={styles.spin} /> : <Trash2 size={16} />}
                      Eliminar
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
}

function normalizeCameraFormat(format?: string | null) {
  const normalized = String(format || "").toLowerCase();
  if (normalized === "png") {
    return { extension: "png", mimeType: "image/png" };
  }
  return { extension: "jpg", mimeType: "image/jpeg" };
}

function formatFileSize(size: number) {
  if (!Number.isFinite(size) || size <= 0) {
    return "Tamano desconocido";
  }
  if (size < 1024 * 1024) {
    return `${Math.max(1, Math.round(size / 1024))} KB`;
  }
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDocumentDate(value?: string) {
  if (!value) {
    return "Fecha no disponible";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Fecha no disponible";
  }

  return date.toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
}

function b64ToBlob(base64: string, mimeType: string) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return new Blob([bytes], { type: mimeType });
}
